import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ProductCard } from "@/components/store/ProductCard";
import { PickupHoursCard } from "@/components/store/PickupHoursCard";
import { ProductImagesModal } from "@/components/admin/ProductImagesModal";
import { useAuth } from "@/context/AuthContext";
import { Search, PackageSearch, Loader2, PackageCheck } from "lucide-react";

const PER_PAGE = 24;
const SORTS = [
  { v: "value_desc", label: "Biggest value first" },
  { v: "price_desc", label: "Price: high to low" },
  { v: "price_asc", label: "Price: low to high" },
  { v: "newest", label: "Newest" },
];

export default function Home() {
  const { user } = useAuth();
  const isAdmin = !!user && (user.permissions || []).includes("MANAGE_PRODUCTS");
  const [settings, setSettings] = useState(null);
  const [avail, setAvail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("value_desc");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Barrhaven Dealswagon Outlet · Find It. Love It. Save Big.";
    api.get("/settings").then((r) => { setSettings(r.data); }).catch(() => {});
    api.get("/stats/availability").then((r) => setAvail(r.data)).catch(() => {});
  }, []);

  const fetchPage = useCallback(async (pageNum, append) => {
    setLoading(true);
    try {
      const r = await api.get("/products", {
        params: { sort, availability: "in_stock", per_page: PER_PAGE, page: pageNum, q: q || undefined, include_hidden: isAdmin || undefined },
      });
      setTotal(r.data.total);
      setItems((prev) => (append ? [...prev, ...r.data.items] : r.data.items));
    } finally {
      setLoading(false);
    }
  }, [sort, q, isAdmin]);

  useEffect(() => {
    setPage(1);
    const t = setTimeout(() => fetchPage(1, false), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchPage, q]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchPage(next, true);
  };

  const toggleHide = async (p) => {
    const nextHidden = !p.hidden;
    setItems((prev) => prev.map((it) => (it.id === p.id ? { ...it, hidden: nextHidden } : it)));
    try {
      await api.post(`/admin/products/${p.id}/visibility`, { hidden: nextHidden });
      toast.success(nextHidden ? "Hidden from storefront" : "Now visible to shoppers");
    } catch {
      setItems((prev) => prev.map((it) => (it.id === p.id ? { ...it, hidden: p.hidden } : it)));
      toast.error("Could not update visibility");
    }
  };

  const onPhotosSaved = async (p) => {
    try {
      const r = await api.get(`/products/${p.slug}`);
      setItems((prev) => prev.map((it) => (it.id === p.id ? { ...it, ...r.data } : it)));
    } catch { /* non-fatal */ }
  };

  const removeProduct = async (p) => {
    if (!window.confirm(`Permanently delete "${p.product_name}"?\n\nThis removes it from the store everywhere and deletes its photos, inventory and history. This CANNOT be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}/permanent`);
      setItems((prev) => prev.filter((it) => it.id !== p.id));
      setTotal((t) => Math.max(0, t - 1));
      toast.success(`"${p.product_name}" permanently deleted`);
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not remove product");
    }
  };

  const hasMore = items.length < total;

  return (
    <main data-testid="home-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <div className="text-brand-red font-black uppercase tracking-widest text-xs mb-1">Shop the warehouse</div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-brand-navy leading-[0.95]">
              Everything in stock, right now
            </h1>
            <p className="text-slate-600 mt-2 max-w-xl">
              Biggest-value finds first. When it's gone, it's gone — grab it before someone else does.
            </p>
            {settings && (
              <div
                data-testid="availability-highlight"
                className="mt-4 flex flex-col items-start gap-2 rounded-2xl sm:rounded-full bg-brand-navy text-white p-2 sm:pr-5 sm:inline-flex sm:flex-row sm:items-center sm:gap-3 shadow-sm"
              >
                <span className="inline-flex items-center gap-1.5 bg-brand-red rounded-full px-3 py-1.5 font-black text-sm uppercase tracking-wide">
                  <PackageCheck className="h-4 w-4" />
                  {avail && avail.available_units > 0
                    ? `${avail.available_units.toLocaleString()} items available`
                    : "Restocking soon"}
                </span>
                <span data-testid="availability-tagline" className="text-sm font-semibold text-white/90 px-1 sm:px-0">
                  {settings.availability_tagline || "All brand new / mint condition only"}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="relative flex-1 sm:w-56">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="home-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search in stock..."
                className="w-full pl-9 pr-3 h-11 rounded-full border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-navy"
              />
            </div>
            <select
              data-testid="home-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="h-11 rounded-full border border-slate-200 bg-white px-3 text-sm font-bold text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-navy"
            >
              {SORTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {loading && items.length === 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white overflow-hidden animate-pulse">
                <div className="aspect-square bg-slate-100" />
                <div className="p-4 space-y-2"><div className="h-3 bg-slate-100 rounded" /><div className="h-3 w-2/3 bg-slate-100 rounded" /></div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20" data-testid="home-empty">
            <PackageSearch className="h-12 w-12 mx-auto text-slate-300" />
            <div className="font-heading text-xl text-brand-navy mt-4">Nothing in stock right now</div>
            <p className="text-slate-500 mt-1">New finds land all the time — check back soon.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="home-product-grid">
              {items.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  testIdPrefix="home"
                  admin={isAdmin}
                  onEditPhotos={setEditing}
                  onToggleHide={toggleHide}
                  onRemove={removeProduct}
                />
              ))}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-10">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  data-testid="home-load-more-btn"
                  className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-red text-white font-black uppercase tracking-wider px-8 h-12 rounded-full active:scale-95 transition disabled:opacity-60"
                >
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Loading</> : `Load more (${total - items.length} left)`}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {settings && <PickupHoursCard settings={settings} />}

      {editing && (
        <ProductImagesModal
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={() => onPhotosSaved(editing)}
        />
      )}

      <div className="h-16" />
    </main>
  );
}
